import React from 'react';
import { useTranslation } from 'react-i18next';
import { IAchievementWithProgress } from 'modules/core/types/gamification';
import { STATIC_IMAGE_URLS } from 'modules/core/constants/staticImagesUrls.const';

import {
  ProgressCardGaugeWrapper,
  ProgressCardGaugeText,
  ProgressCardPoints,
  ProgressCardTitle,
  ProgressCardRow,
  ProgressCardRowInfo,
  RewardButton,
  RewardButtonIcon,
} from './styles';
import { CircularProgress } from './CircularProgress';

export type AchievementContext = 'search' | 'modal' | 'account';
export type AchievementDensity = 'double' | 'triple';

const MODAL_ROW = {
  circle: 36,
  stroke: 6,
  title: '12px',
  count: '12px',
  btn: '9px',
  icon: '8px',
  btnPad: '2px 5px',
  countMargin: '6px 0',
  gap: '12px',
  pad: '10px 10px',
} as const;
const ACCOUNT_ROW = {
  circle: 44,
  stroke: 6,
  title: '14px',
  count: '13px',
  btn: '10px',
  icon: '13px',
  btnPad: '5px 10px',
  countMargin: '4px 0',
  gap: '14px',
  pad: '25px 16px',
} as const;

export const VARIANTS = {
  search: {
    single: { active: { circle: 120, stroke: 12, icon: 79 }, completed: { circle: 100, stroke: 10, icon: 41 } },
    double: {
      circle: 60,
      stroke: 8,
      title: '15px',
      count: '14px',
      btn: '12px',
      icon: '13px',
      btnPad: '5px 10px',
      countMargin: '8px 0',
      gap: '25px',
      pad: '10px 25px',
    },
    triple: {
      circle: 40,
      stroke: 6,
      title: '12px',
      count: '11px',
      btn: '9px',
      icon: '10px',
      btnPad: '5px 10px',
      countMargin: '4px 0',
      gap: '14px',
      pad: '10px 16px',
    },
  },
  modal: {
    single: { active: { circle: 95, stroke: 8, icon: 40 }, completed: { circle: 50, stroke: 6, icon: 25 } },
  },
} as const;

export interface IAchievementListRowProps {
  achievement: IAchievementWithProgress;
  percentage: number;
  context: AchievementContext;
  /** Only used when `context === 'search'`; ignored for `'modal'` and `'account'`. */
  density?: AchievementDensity;
  isCompleted?: boolean;
}

const getRowConfig = (context: AchievementContext, density: AchievementDensity = 'triple') => {
  if (context === 'account') return ACCOUNT_ROW;

  if (context === 'modal') return MODAL_ROW;

  return VARIANTS.search[density];
};

export const AchievementListRow: React.FC<IAchievementListRowProps> = ({
  achievement: a,
  percentage,
  context,
  density,
  isCompleted = false,
}) => {
  const { t } = useTranslation();
  const v = getRowConfig(context, density);
  const reserveRewardRow = context === 'account';

  const actionLabel =
    a.activity_type === 'submission' ? t('gamification.badge.submissions') : t('gamification.badge.searches');

  const rewardButton = a.reward ? (
    <RewardButton
      href={a.reward.link_url || undefined}
      target={a.reward.link_url ? '_blank' : undefined}
      rel={a.reward.link_url ? 'noopener noreferrer' : undefined}
      $disabled={!isCompleted || !a.reward.link_url}
      $btnFontSize={v.btn}
      $padding={v.btnPad}
    >
      <RewardButtonIcon
        src={STATIC_IMAGE_URLS.icons.unlockWhiteBackground}
        alt=""
        $disabled={!isCompleted || !a.reward.link_url}
        $iconHeight={v.icon}
      />
      {a.reward.link_label || t('gamification.progressCard.claimReward')}
    </RewardButton>
  ) : null;

  return (
    <ProgressCardRow $gap={v.gap} $padding={v.pad}>
      <ProgressCardGaugeWrapper $compact $gaugeSize={v.circle}>
        <CircularProgress percentage={percentage} size={v.circle} strokeWidth={v.stroke} />
        {isCompleted && (
          <ProgressCardGaugeText>
            <img src={STATIC_IMAGE_URLS.icons.checkBlueSmallCompletion} alt="" width={v.circle / 2.5} />
          </ProgressCardGaugeText>
        )}
      </ProgressCardGaugeWrapper>

      <ProgressCardRowInfo>
        <ProgressCardTitle $small $fontSize={v.title}>
          {a.name}
        </ProgressCardTitle>

        <ProgressCardPoints $small $fontSize={v.count} $margin={v.countMargin}>
          {a.current_count}/{a.threshold} {actionLabel}
        </ProgressCardPoints>

        {(reserveRewardRow || rewardButton) && <div>{rewardButton}</div>}
      </ProgressCardRowInfo>
    </ProgressCardRow>
  );
};
